/** `x-widget: "json"` — a generic JSON object editor (used for
 *  `restech_migration`: an object keyed by booking-system code → object).
 *
 * The grid cell shows a "{N keys}" summary and an "Edit JSON…" button that
 * opens a `<Dialog maxWidth="md">` with a monospace multiline TextField holding
 * pretty-printed JSON. On save the text is parsed; invalid JSON shows an inline
 * error and keeps the dialog open. Uses a plain `<TextField multiline>` — never
 * `dangerouslySetInnerHTML`.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface CellProps {
  value: unknown;
  onChange: (next: unknown) => void;
  readOnly?: boolean;
}

function pretty(value: unknown): string {
  if (value === undefined) return '{}';
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

function keyCount(value: unknown): number {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value as Record<string, unknown>).length;
  }
  if (Array.isArray(value)) return value.length;
  return 0;
}

interface DialogProps {
  open: boolean;
  value: unknown;
  readOnly: boolean;
  onClose: () => void;
  onSave: (parsed: unknown) => void;
}

function JsonDialog({ open, value, readOnly, onClose, onSave }: DialogProps) {
  const [text, setText] = useState(() => pretty(value));
  const [error, setError] = useState<string | null>(null);

  // Re-seed the textarea whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setText(pretty(value));
      setError(null);
    }
  }, [open, value]);

  function handleSave() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
      return; // keep the dialog open
    }
    setError(null);
    onSave(parsed);
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit JSON</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        <TextField
          multiline
          fullWidth
          minRows={12}
          maxRows={28}
          value={text}
          disabled={readOnly}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          slotProps={{
            htmlInput: {
              style: { fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre' },
              autoCapitalize: 'off',
              autoCorrect: 'off',
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={readOnly}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function JsonEditorCell({ value, onChange, readOnly = false }: CellProps) {
  const [open, setOpen] = useState(false);
  const n = keyCount(value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        sx={{ fontFamily: 'monospace', minWidth: 0, flex: 1 }}
      >
        {`{${n} key${n !== 1 ? 's' : ''}}`}
      </Typography>
      <Button
        size="small"
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {readOnly ? 'View JSON…' : 'Edit JSON…'}
      </Button>
      <JsonDialog
        open={open}
        value={value}
        readOnly={readOnly}
        onClose={() => setOpen(false)}
        onSave={(parsed) => {
          onChange(parsed);
          setOpen(false);
        }}
      />
    </Box>
  );
}
